import { Box, Text } from '@mantine/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { brand } from '../../theme/brand';
import { artEntry, artKeyFor, orientationOf } from './floor-art.generated';
import { cssRadius, isRunKind, kindLabel, materialFor, runArt, type WeldSpans } from './shapes';

export interface Spot {
  id: string;
  /** False for scenery — walls, the dock, palms. No tab can be opened against it. */
  assignable?: boolean;
  /**
   * Drawn given a half-turn within the same footprint: the sign's arrow points the other way, the
   * counter's guest rail sits on the opposite side, the lounger's backrest moves to the other end.
   * The column exists on the server already; it is declared by hand here until the generated API
   * schema picks it up on the next contract emit.
   */
  flipped?: boolean;
  label: string;
  kind: string;
  x: number;
  y: number;
  width: number;
  height: number;
  shape: string;
  seats: number;
  mobility: string;
  combinable: boolean;
  active: boolean;
}

/** Positions snap to this so an arrangement stays tidy without anyone lining things up by hand. */
const SNAP = 20;
const PADDING = 24;
/** Nothing can be dragged down to nothing — a shape with no size is a shape you cannot grab again. */
const MIN_SIZE = SNAP;
/**
 * The smallest a stretch may leave this side — never bigger than it already is.
 *
 * A wall arrives sixteen deep, which is under that floor. Applying the floor flat meant taking hold
 * of a wall to run it further along the terrace also made it thicker, because the drag rewrote a
 * thickness nobody had touched — and two walls of different thickness no longer line up. The floor
 * is here to stop a shape disappearing, not to fatten one that was always thinner than it.
 */
const floorFor = (current: number) => Math.min(MIN_SIZE, current);
/** The grab area on a handle. Sized for a thumb, because this gets used on a phone behind the bar. */
const HANDLE_HIT = 32;
/** What is actually drawn. Small enough not to hide a bar stool it is sitting on. */
const HANDLE_DOT = 14;

const snap = (n: number) => Math.round(n / SNAP) * SNAP;

/**
 * How close an edge has to come before it stops obeying the grid and takes its neighbor's line.
 *
 * The grid alone is why "when I try to connect walls they don't really connect": two segments both
 * land on grid lines a whole square apart and the corner of the room has a gap in it. Under this
 * distance the neighbor wins, so the second wall ends exactly where the first one starts.
 *
 * Deliberately less than half a grid square. At ten a shape could never be parked on a grid line
 * next to something, because the neighbor would always be nearer — a snap you cannot get out of.
 */
const ALIGN = 8;

/**
 * A line something can line up with, and how far the thing that owns it runs across the plan.
 *
 * The span is only there so the guide we draw reaches from one shape to the other, the way it does
 * in a drawing program — a line across the whole floor tells you a snap happened but not to what.
 */
type Line = { at: number; from: number; to: number };

/** A line worth drawing while the drag is happening, already stretched to cover both shapes. */
type Guide = { axis: 'x' | 'y'; at: number; from: number; to: number };

/** The closest line to any of the edges being dragged, or nothing if none is within reach. */
function nearestLine(mine: number[], lines: Line[]): { delta: number; line: Line } | null {
  let best: { delta: number; line: Line } | null = null;
  for (const line of lines) {
    for (const m of mine) {
      const delta = line.at - m;
      if (Math.abs(delta) > ALIGN) continue;
      if (best && Math.abs(delta) >= Math.abs(best.delta)) continue;
      best = { delta, line };
    }
  }
  return best;
}

/**
 * Everything already on the plan that a shape could line itself up with, on one axis.
 *
 * Edges and middles are kept apart because they are not interchangeable: a run of wall stays
 * straight by sharing a middle, and a corner closes by an edge meeting an edge. Letting a middle
 * land on an edge would jump a wall half its own thickness for no reason anyone could see.
 */
function linesAlong(
  others: Spot[],
  axis: 'x' | 'y',
  limit: number | null,
  crossLimit: number | null,
): { edges: Line[]; centers: Line[] } {
  const edges: Line[] = [];
  const centers: Line[] = [];
  for (const o of others) {
    const start = axis === 'x' ? o.x : o.y;
    const size = axis === 'x' ? o.width : o.height;
    const from = axis === 'x' ? o.y : o.x;
    const to = from + (axis === 'x' ? o.height : o.width);
    edges.push({ at: start, from, to }, { at: start + size, from, to });
    centers.push({ at: start + size / 2, from, to });
  }
  // The outline of the area is an edge too. A wall meant to BE the side of the beach should sit on
  // the line the owner drew, not one square inside it.
  if (limit !== null) {
    const to = crossLimit ?? 0;
    edges.push({ at: 0, from: 0, to }, { at: limit, from: 0, to });
  }
  return { edges, centers };
}

/**
 * Where a dragged edge actually lands: a neighbor's line if one is in reach, else the grid.
 *
 * `free` is the escape hatch — held down, nothing snaps at all and the shape goes exactly where the
 * finger is. Something that can only ever be placed on a line is unusable the one time the room
 * does not agree with the grid.
 */
function settle(
  raw: number,
  myEdges: number[],
  myCenter: number | null,
  lines: { edges: Line[]; centers: Line[] },
  free: boolean,
): { value: number; guide: Line | null } {
  if (free) return { value: Math.round(raw), guide: null };
  const byEdge = nearestLine(myEdges, lines.edges);
  const byCenter = myCenter === null ? null : nearestLine([myCenter], lines.centers);
  // Edge beats middle on a tie: closing a gap is what was asked for, keeping a run straight is
  // what makes it look right afterwards.
  const hit = !byCenter || (byEdge && Math.abs(byEdge.delta) <= Math.abs(byCenter.delta)) ? byEdge : byCenter;
  if (!hit) return { value: snap(raw), guide: null };
  // Positions go to the server as whole numbers; a middle-to-middle match on two odd sizes is the
  // one case that produces a half.
  return { value: Math.round(raw + hit.delta), guide: hit.line };
}

/** Stretch each line that got used so it reaches from the neighbor to the shape being dragged. */
function drawnGuides(
  across: Line | null,
  down: Line | null,
  x: number,
  y: number,
  width: number,
  height: number,
): Guide[] {
  const out: Guide[] = [];
  if (across) {
    const from = Math.min(across.from, y);
    out.push({ axis: 'x', at: across.at, from, to: Math.max(across.to, y + height) });
  }
  if (down) {
    const from = Math.min(down.from, x);
    out.push({ axis: 'y', at: down.at, from, to: Math.max(down.to, x + width) });
  }
  return out;
}

/**
 * How far one run's end reaches for another's — a full grid square, where ALIGN reaches eight.
 *
 * The owner's L-shaped bar top is why the general reach cannot close a corner: place two counters
 * by hand and the grid parks each of them ON a grid line, so the natural miss between their ends
 * is exactly one square — twenty units, which an eight-unit reach never touches. "Endpoints aren't
 * connecting to create a proper corner" is that gap. Between the ends of two runs of the SAME kind
 * a whole square is safe to claim, because a counter's end one square shy of another counter's end
 * is always a corner that failed to shut, never an arrangement anyone wanted — which is not true
 * of shapes in general, and is why ALIGN itself must stay under half a square.
 */
const JOIN = SNAP;

/** A footprint's four corners. A run's two ends carry two corners each — the corners ARE its endpoints. */
const cornersOf = (r: { x: number; y: number; width: number; height: number }) => [
  { x: r.x, y: r.y },
  { x: r.x + r.width, y: r.y },
  { x: r.x, y: r.y + r.height },
  { x: r.x + r.width, y: r.y + r.height },
];

/**
 * Whether two footprints abut: axis-aligned, an edge of one lying exactly along an edge of the
 * other, with some length actually shared. This is the same definition `weldsFor` closes seams
 * by, on purpose — a snap judged by it can only ever land a joint the drawing will then weld
 * shut. Exact equality is the touching test, so overlapping footprints fail it (their edges cross
 * instead of meeting), and the shared-length test throws out a corner-to-corner touch, which
 * looks joined at a glance and comes apart the moment anyone looks closer.
 */
function abut(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
): boolean {
  const sharedX = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const sharedY = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  if ((a.x + a.width === b.x || b.x + b.width === a.x) && sharedY > 0) return true;
  return (a.y + a.height === b.y || b.y + b.height === a.y) && sharedX > 0;
}

/**
 * The shift that shuts a joint while a run is being MOVED: the smallest move, at most one square
 * on each axis, that lands one of this run's corners exactly on a corner of another run of the
 * same kind with the two left flush against each other.
 *
 * Corner onto corner is what "connected" means for both joints a bar has — end to end, where the
 * second counter carries the first one on, and the L, where the two share an outer corner. Trying
 * every pairing and keeping the nearest one that truly abuts lets the owner's hand choose which
 * of those a given approach means, instead of this code guessing. The two lines returned are the
 * joint's own edges, drawn so the snap reads as "it grabbed the corner" rather than as the plan
 * fighting the drag.
 */
function joinRun(
  spot: Spot,
  x: number,
  y: number,
  others: Spot[],
): { x: number; y: number; toX: Line; toY: Line } | null {
  if (!spot.active || !isRunKind(spot.kind)) return null;
  const mine = cornersOf({ x, y, width: spot.width, height: spot.height });
  let best: { score: number; x: number; y: number; toX: Line; toY: Line } | null = null;
  for (const n of others) {
    if (n.kind !== spot.kind || !n.active) continue;
    for (const target of cornersOf(n)) {
      for (const corner of mine) {
        const dx = target.x - corner.x;
        const dy = target.y - corner.y;
        if (Math.abs(dx) > JOIN || Math.abs(dy) > JOIN) continue;
        const landed = { x: x + dx, y: y + dy, width: spot.width, height: spot.height };
        if (!abut(landed, n)) continue;
        const score = dx * dx + dy * dy;
        if (best && best.score <= score) continue;
        best = {
          score,
          x: landed.x,
          y: landed.y,
          toX: { at: target.x, from: n.y, to: n.y + n.height },
          toY: { at: target.y, from: n.x, to: n.x + n.width },
        };
      }
    }
  }
  return best;
}

/**
 * The same joint-shutting pull for a STRETCH, which may only give on the axis the handle owns.
 *
 * The moving edge's two corners reach for a same-kind corner within a square of them, and the
 * length snaps so the runs abut exactly. The cross axis is the trigger's business but never the
 * snap's: a stretch that also slid the run sideways would be the corner-handle defect — a gesture
 * rewriting a dimension it does not own — let back in through this door. An end the cross axis
 * has not quite reached is left unsnapped on purpose, because the still-open outline is exactly
 * what tells the owner the joint needs the other drag.
 */
function joinStretch(
  axis: 'x' | 'y',
  spot: Spot,
  edgeAt: number,
  crossFrom: number,
  crossTo: number,
  others: Spot[],
): { at: number; guide: Line } | null {
  if (!spot.active || !isRunKind(spot.kind)) return null;
  let best: { score: number; at: number; guide: Line } | null = null;
  for (const n of others) {
    if (n.kind !== spot.kind || !n.active) continue;
    for (const target of cornersOf(n)) {
      const along = axis === 'x' ? target.x : target.y;
      const cross = axis === 'x' ? target.y : target.x;
      if (Math.abs(along - edgeAt) > JOIN) continue;
      if (Math.abs(cross - crossFrom) > JOIN && Math.abs(cross - crossTo) > JOIN) continue;
      const landed =
        axis === 'x'
          ? { x: spot.x, y: crossFrom, width: along - spot.x, height: crossTo - crossFrom }
          : { x: crossFrom, y: spot.y, width: crossTo - crossFrom, height: along - spot.y };
      if (landed.width <= 0 || landed.height <= 0 || !abut(landed, n)) continue;
      const score = Math.abs(along - edgeAt);
      if (best && best.score <= score) continue;
      best = {
        score,
        at: along,
        guide:
          axis === 'x'
            ? { at: target.x, from: n.y, to: n.y + n.height }
            : { at: target.y, from: n.x, to: n.x + n.width },
      };
    }
  }
  return best;
}

/**
 * A defined area is a real edge — but only for things that fit inside it.
 *
 * The clamp used to read `Math.max(0, limit - size)`, which for anything WIDER than the area
 * collapses to zero: a bar counter is 220 across, so dropping one into a 200-wide area pinned it to
 * the left edge and then re-pinned it on every drag, so it could never be moved again. The owner hit
 * exactly that — "as I'm trying to setup a counter or wall it's getting stuck in the corner".
 *
 * Something too big for the room is a thing the owner is in the middle of sorting out: either he is
 * about to stretch the area or shrink the counter. Freezing it in a corner helps with neither. So a
 * limit only applies to something that can actually honor it; anything larger is left where it is
 * put and reported as outside, which is the honest state and the one he can act on.
 */
const fitInside = (v: number, size: number, limit: number | null) => {
  if (limit === null || size >= limit) return Math.max(0, v);
  return Math.max(0, Math.min(v, limit - size));
};

/**
 * The three ways to stretch something, and where each one sits.
 *
 * A wall is stretched along one axis and the dock in both, so width, height and the pair each get
 * their own grab point. Right, bottom and the corner between them only — a handle on the left or top
 * edge would move the thing as well as resize it, and "it got bigger AND slid left" is not what
 * anybody dragging a wall out means. Stretch right, then drag it back into place.
 *
 * The corner is listed last so it paints over the other two where their grab areas still touch,
 * which is the way round anyone aiming at a corner expects. On anything too thin to give all three
 * a target of their own it is not offered at all — see `handlesFor`.
 */
const HANDLES: {
  axis: 'x' | 'y' | 'xy';
  cursor: string;
  style: React.CSSProperties;
}[] = [
  {
    axis: 'x',
    cursor: 'ew-resize',
    style: { right: -HANDLE_HIT / 2, top: '50%', marginTop: -HANDLE_HIT / 2 },
  },
  {
    axis: 'y',
    cursor: 'ns-resize',
    style: { bottom: -HANDLE_HIT / 2, left: '50%', marginLeft: -HANDLE_HIT / 2 },
  },
  { axis: 'xy', cursor: 'nwse-resize', style: { right: -HANDLE_HIT / 2, bottom: -HANDLE_HIT / 2 } },
];

/**
 * The grab points a shape is actually big enough to carry.
 *
 * A wall arrives sixteen deep and a grab area is thirty-two across, so on a wall the corner's box
 * covers three quarters of the length-only one and — being painted last — wins every press. Aiming
 * at the dot that only makes the wall longer got the corner instead, and the corner then places the
 * bottom edge too: a dead-straight sideways drag on the sea wall came back 20 deep instead of 16,
 * a thickness nobody had touched. Two walls of different thickness no longer meet, which is the
 * owner's "the walls don't really connect" coming back in through the side door.
 *
 * There is no room to move the corner out of the way — on a sixteen-deep wall the two grab points
 * are eight apart, and no arrangement gives both a target worth aiming at. So on something that
 * thin the corner is dropped instead. Length and depth keep a grab point each, well apart, and
 * neither can ever rewrite the other; standing a wall on end is two drags rather than one. Anything
 * a table's size is unchanged and still resizes both ways in a single gesture.
 */
const handlesFor = (width: number, height: number) =>
  width < HANDLE_HIT || height < HANDLE_HIT ? HANDLES.filter((h) => h.axis !== 'xy') : HANDLES;

/**
 * The stretches of this run's edges that lie flush against another run of the same kind.
 *
 * The owner's bar interior has an L-shaped bar top: two counter runs meeting at a corner. The
 * alignment snap already put their endpoints exactly flush, but each run drew its own complete
 * outline, so however perfectly they met they still read as two boxes with a seam down the joint
 * — his words, "two disconnected elements". Every stretch of edge found here is left undrawn by
 * `runArt`, and the two runs become one shape with one outline.
 *
 * "Abutting" is flush to the exact unit, with some length actually shared. Not a tolerance:
 * the alignment snap is what produces flush joins, so a joint the owner has snapped shut IS
 * exactly equal — and anything merely near still keeps its own outline, which is precisely
 * what tells him the joint has not closed yet.
 *
 * A selected run keeps its whole outline: the selection is drawn AS the fill and outline, and
 * welding a picked teal run into an unpicked sand one would erase the boundary between two
 * different colors in the middle of an edit. Runs off the plan never weld either — being visibly
 * a separate, struck-out thing is the whole point of still drawing them.
 */
function weldsFor(spot: Spot, all: Spot[], selection: Set<string>): WeldSpans {
  const welds: WeldSpans = { top: [], right: [], bottom: [], left: [] };
  if (!spot.active || selection.has(spot.id)) return welds;
  for (const n of all) {
    if (n.id === spot.id || n.kind !== spot.kind || !n.active || selection.has(n.id)) continue;
    const x0 = Math.max(spot.x, n.x);
    const x1 = Math.min(spot.x + spot.width, n.x + n.width);
    if (x1 > x0) {
      if (n.y + n.height === spot.y) welds.top.push([x0 - spot.x, x1 - spot.x]);
      if (spot.y + spot.height === n.y) welds.bottom.push([x0 - spot.x, x1 - spot.x]);
    }
    const y0 = Math.max(spot.y, n.y);
    const y1 = Math.min(spot.y + spot.height, n.y + n.height);
    if (y1 > y0) {
      if (n.x + n.width === spot.x) welds.left.push([y0 - spot.y, y1 - spot.y]);
      if (spot.x + spot.width === n.x) welds.right.push([y0 - spot.y, y1 - spot.y]);
    }
  }
  return welds;
}

/**
 * The weld spans restated for a drawing that is about to be given a half-turn.
 *
 * `flipped` turns a run's DRAWING around; its footprint, and therefore where its neighbors
 * actually touch it, never moves. Left alone, the rotation would carry an open outline edge to
 * the opposite side of the box — a welded joint suddenly drawn shut, and an outline missing where
 * open floor is. So the spans are turned the same half-turn first and the two rotations cancel:
 * the gap in the outline stays on the edge the neighbor is really on, while the guest rail — the
 * thing the flip is FOR — comes out on the other side.
 */
function flipWelds(welds: WeldSpans, width: number, height: number): WeldSpans {
  const spin = (spans: [number, number][], length: number): [number, number][] =>
    spans.map(([a, b]) => [length - b, length - a]);
  return {
    top: spin(welds.bottom, width),
    bottom: spin(welds.top, width),
    left: spin(welds.right, height),
    right: spin(welds.left, height),
  };
}

/**
 * Scenery is drawn to recede. A wall and the dock are there so the plan looks like the property —
 * they are landmarks that make the seating findable, not things competing with it for attention.
 *
 * The colors themselves are in `contract/floor-art.json`. They used to be written out here and
 * hand-copied into the register, whose own copy carried a comment saying so; the copy had already
 * drifted by the time anybody looked.
 */
const decorStyle = (kind: string) => materialFor(kind);

/**
 * What to write inside a shape that is too small for a full name.
 *
 * A row of twelve beach chairs is labelled "Chair 1" … "Chair 12", which does not fit in a 32-unit
 * circle and spills across its neighbors. Real floor plans solve this the same way: the zone tells
 * you they are chairs, so the shape only has to carry the number. The full name stays in the
 * tooltip and the accessible name, so nothing is actually lost.
 */
function fitLabel(label: string, width: number): string {
  if (width >= 56) return label;
  const trailingNumber = label.match(/(\d+)\s*$/)?.[1];
  return trailingNumber ?? label.slice(0, 3);
}

/**
 * The room, drawn.
 *
 * The database has held x/y coordinates for every table since the first migration, and nothing
 * ever rendered them — so arranging the floor meant typing two numbers into boxes labelled "x"
 * and "y" and imagining the result. This is the other half of that schema.
 *
 * Dragging is deliberately plain pointer events on absolutely-positioned boxes rather than a
 * drag-and-drop library: the interaction is a handful of lines, it works with touch on a phone
 * behind the bar, and it adds no dependency that has to survive a decade of React versions.
 */
export function FloorMap({
  spots,
  selectedIds,
  onSelect,
  onMove,
  onResize,
  onAddAt,
  onOpen,
  readOnly,
  placing,
  room,
  onRoomResize,
  onExtentChange,
}: {
  spots: Spot[];
  /**
   * Everything picked out right now. Rearranging a bar means shifting a row of stools together, not
   * one stool eleven times, so the plan carries a selection rather than a single chosen thing.
   */
  selectedIds?: string[];
  /**
   * Reports back the WHOLE selection, not the thing that was clicked. Shift-clicking, dragging a box
   * round a cluster and clicking bare floor each mean something different, and working out which is
   * the map's job — the page above only has to remember the answer.
   */
  onSelect?: (ids: string[]) => void;
  onMove?: (id: string, x: number, y: number) => void;
  /** Drag a handle on the selected thing to stretch it. Staged exactly like a move. */
  onResize?: (id: string, width: number, height: number) => void;
  /** Click empty floor to put something there. The position is the point that was clicked. */
  onAddAt?: (x: number, y: number) => void;
  /** Something is armed on the palette, so a single click on bare floor places it. */
  placing?: boolean;
  /** Right-click (or double-click) a place to edit it where it stands. */
  onOpen?: (id: string) => void;
  readOnly?: boolean;
  /** The shape the area has been given, if any. Without one the plan grows to fit its contents. */
  room?: { width?: number | null; height?: number | null } | null;
  /**
   * Dragging the room's own corner. Without it the only way to shape a room is to type two numbers
   * with nothing on screen saying what numbers would be right — every element can be stretched by
   * hand except the one thing whose proportions the owner is actually trying to get correct.
   */
  onRoomResize?: (width: number, height: number) => void;
  /** How far the furniture reaches, and what is past the edge, so the page can say both. */
  onExtentChange?: (info: { contentW: number; contentH: number; outside: number }) => void;
}) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  /**
   * A move in progress, and everything it is carrying.
   *
   * `origin` is where each picked shape stood when the press happened, not where it is now: the
   * whole group is placed from one delta off those, so a row of stools keeps its spacing instead of
   * drifting apart as each one is clamped separately. `bounds` is the group's outline at that same
   * moment, which is what stops at the wall — the group hits the edge together or not at all.
   */
  const [dragging, setDragging] = useState<{
    id: string;
    dx: number;
    dy: number;
    origin: { id: string; x: number; y: number }[];
    bounds: { minX: number; minY: number; maxX: number; maxY: number };
  } | null>(null);
  /**
   * A box being dragged across bare floor to gather things up.
   *
   * `keep` is what was already picked when it started, so holding shift and lassoing a second
   * cluster adds to the first instead of throwing it away.
   */
  const [marquee, setMarquee] = useState<{
    x0: number;
    y0: number;
    x: number;
    y: number;
    keep: string[];
  } | null>(null);
  /**
   * A stretch in progress. `grab` is how far the pointer was from the edge it took hold of, kept so
   * the edge follows the finger instead of jumping to sit under it the moment the drag starts.
   *
   * `startW` and `startH` are the size the shape had when the handle was grabbed. The smallest a
   * drag may leave a side, and whether the area's edge gets a say over it at all, are both judged
   * against that size and not against whatever the last pointer event staged — judged live, the
   * rules shift under the hand in mid-drag, which is how the owner's bar top got destroyed rather
   * than resized (see the clamp in the move handler).
   */
  const [resizing, setResizing] = useState<{
    id: string;
    axis: 'x' | 'y' | 'xy';
    grabX: number;
    grabY: number;
    startW: number;
    startH: number;
  } | null>(null);
  /**
   * The lines currently being lined up with, drawn while the drag is happening.
   *
   * Without them a shape jumping the last few pixels looks like the plan fighting you. With them it
   * is obviously the wall next door, and the owner learns where things will land.
   */
  const [guides, setGuides] = useState<Guide[]>([]);
  /**
   * A press on something that was already picked, waiting to find out what it meant.
   *
   * It cannot be answered until the finger comes up. Taking hold of one of six tables has to keep
   * all six, or the group can never be dragged — but a plain CLICK on one of six means that one,
   * which is how anybody narrows a selection down and how this screen behaved before it could hold
   * more than one thing. So the group is kept for the duration of the press, and if nothing
   * actually moved, the release settles on the one that was clicked.
   *
   * A ref rather than state: it is written and read inside the same gesture, and a re-render in the
   * middle of a drag is exactly what must not happen.
   */
  const narrowTo = useRef<string | null>(null);

  /**
   * The selection, narrowed to this area's own plan.
   *
   * Every area draws its own map and they all share one selection. Without this filter, shift-
   * clicking a palm on the beach would quietly join it to a table picked on the deck, and the next
   * drag would move both — across two rooms that have nothing to do with each other.
   */
  const here = new Set(spots.map((s) => s.id));
  const picked = (selectedIds ?? []).filter((id) => here.has(id));
  const selection = new Set(picked);

  const limitW = room?.width ?? null;
  const limitH = room?.height ?? null;
  /**
   * An area counts as SHAPED only once it has both numbers. A zone object always arrives — every
   * area is one — so testing the object rather than its size drew a room outline of `undefined`
   * around every plan that had never been given one.
   */
  const shaped = limitW !== null && limitH !== null;

  // Without a size of its own the plan sizes itself to whatever is on it, so a zone with six tables
  // does not render inside an arbitrary fixed rectangle with the furniture huddled in one corner.
  // With one, the drawn area is the room — but the surface still stretches to reach anything that
  // was already sitting outside it, because a table that disappears the moment an area is given a
  // size is a table its owner has no way of getting back.
  const contentX = Math.max(...spots.map((s) => s.x + s.width), shaped ? 0 : 320) + PADDING;
  const contentY = Math.max(...spots.map((s) => s.y + s.height), shaped ? 0 : 220) + PADDING;
  /**
   * A sized area draws at the size it was given — not at the size of whatever happens to be on it.
   *
   * This used to be `Math.max(limit, content)`, so typing a SMALLER number than the furniture
   * reached did nothing visible: the outline moved, the surface did not, and the owner was left
   * moving things inside an invisible boundary that did not match the room he could see. Setting the
   * size is the whole point of the field, so the size wins. Anything left outside is still drawn and
   * counted rather than hidden, because a table you cannot see is a table you cannot drag back in.
   */
  const extentX = shaped ? (limitW ?? 0) + PADDING : contentX;
  const extentY = shaped ? (limitH ?? 0) + PADDING : contentY;

  /** What is sticking out past the edge the owner drew, so the screen can say so rather than hide it. */
  const outside = !shaped
    ? []
    : spots.filter((s) => s.x + s.width > (limitW ?? 0) || s.y + s.height > (limitH ?? 0));

  // Reported up rather than recomputed there: the page would have to repeat the same arithmetic over
  // the same spots, and the two would drift the first time a footprint changed.
  const [roomDrag, setRoomDrag] = useState(false);

  const contentW = Math.max(0, ...spots.map((s) => s.x + s.width));
  const contentH = Math.max(0, ...spots.map((s) => s.y + s.height));
  const outsideCount = outside.length;
  useEffect(() => {
    onExtentChange?.({ contentW, contentH, outside: outsideCount });
  }, [contentW, contentH, outsideCount, onExtentChange]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, spot: Spot) => {
      // Shift on any machine, and cmd on a Mac or ctrl elsewhere — whichever modifier the owner
      // reaches for, it adds to what is picked rather than replacing it.
      const adding = e.shiftKey || e.metaKey || e.ctrlKey;
      // Pressing on something already picked leaves the group alone, which is what makes dragging
      // the whole selection possible: taking hold of one of six tables must not drop the other five.
      const next = adding
        ? selection.has(spot.id)
          ? picked.filter((id) => id !== spot.id)
          : [...picked, spot.id]
        : selection.has(spot.id)
          ? picked
          : [spot.id];
      onSelect?.(next);
      // Left button only: a right-click on one of several picked things opens THAT one's editor and
      // is explicitly not allowed to throw the rest of the group away.
      narrowTo.current =
        e.button === 0 && !adding && selection.has(spot.id) && picked.length > 1 ? spot.id : null;
      // Shift-clicking to UNpick something is not the start of a drag of it.
      if (readOnly || !onMove || !next.includes(spot.id)) return;
      const surface = surfaceRef.current?.getBoundingClientRect();
      if (!surface) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      const moving = spots.filter((s) => next.includes(s.id));
      setDragging({
        id: spot.id,
        dx: e.clientX - surface.left - spot.x,
        dy: e.clientY - surface.top - spot.y,
        origin: moving.map((s) => ({ id: s.id, x: s.x, y: s.y })),
        bounds: {
          minX: Math.min(...moving.map((s) => s.x)),
          minY: Math.min(...moving.map((s) => s.y)),
          maxX: Math.max(...moving.map((s) => s.x + s.width)),
          maxY: Math.max(...moving.map((s) => s.y + s.height)),
        },
      });
    },
    [onMove, onSelect, picked, readOnly, selection, spots],
  );

  /**
   * Taking hold of a handle stretches and does nothing else.
   *
   * `stopPropagation` is the whole of it: without it the same press also starts the body drag
   * underneath, and the wall would slide across the room while it grew. Handles only appear on the
   * thing already selected, so nothing is lost by not selecting again here.
   */
  const handleResizeDown = useCallback(
    (e: React.PointerEvent, spot: Spot, axis: 'x' | 'y' | 'xy') => {
      if (readOnly || !onResize) return;
      e.stopPropagation();
      e.preventDefault();
      const surface = surfaceRef.current?.getBoundingClientRect();
      if (!surface) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      setResizing({
        id: spot.id,
        axis,
        grabX: e.clientX - surface.left - (spot.x + spot.width),
        grabY: e.clientY - surface.top - (spot.y + spot.height),
        startW: spot.width,
        startH: spot.height,
      });
    },
    [onResize, readOnly],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const surface = surfaceRef.current?.getBoundingClientRect();
      if (!surface) return;
      if (roomDrag && onRoomResize) {
        // The room never shrinks below one grid square; a zero-sized area would swallow the handle
        // that resizes it and there would be no way back.
        onRoomResize(
          Math.max(SNAP * 4, snap(e.clientX - surface.left)),
          Math.max(SNAP * 4, snap(e.clientY - surface.top)),
        );
        return;
      }
      // Holding it down turns every kind of snapping off, grid included, for as long as it is held.
      const free = e.altKey;
      if (resizing && onResize) {
        const spot = spots.find((s) => s.id === resizing.id);
        if (!spot) return;
        const others = spots.filter((s) => s.id !== resizing.id);
        const wide = resizing.axis !== 'y';
        const deep = resizing.axis !== 'x';
        // A stretch moves one edge, so only that edge looks for a neighbor — pulling a wall out to
        // meet the next one is the whole point of stretching it. Middles are left out: changing how
        // long something is to make two middles agree is not a thing anyone means to do.
        const rawRight = e.clientX - surface.left - resizing.grabX;
        const rawBottom = e.clientY - surface.top - resizing.grabY;
        const right = wide
          ? settle(rawRight, [rawRight], null, linesAlong(others, 'x', limitW, limitH), free)
          : null;
        const bottom = deep
          ? settle(rawBottom, [rawBottom], null, linesAlong(others, 'y', limitH, limitW), free)
          : null;
        // The floors come from the size at the grab, not the size the last pointer event staged.
        // Measured live, the floor ratchets: a sixteen-deep wall dragged past twenty and pulled
        // back found the floor had risen to twenty behind it, and the thickness it started the
        // drag at could never be returned to.
        const minW = floorFor(resizing.startW);
        const minH = floorFor(resizing.startH);
        let targetRight = right ? right.value : null;
        let targetBottom = bottom ? bottom.value : null;
        let rightGuide = right?.guide ?? null;
        let bottomGuide = bottom?.guide ?? null;
        // A run's moving end then reaches a whole square for a same-kind end, over whatever the
        // grid or the lines just said: the owner could stretch a counter TOWARD the other counter,
        // but the grid only ever offered him positions a square apart and kept the last of the gap.
        if (!free) {
          const stagedW = targetRight === null ? spot.width : Math.max(minW, targetRight - spot.x);
          const stagedH = targetBottom === null ? spot.height : Math.max(minH, targetBottom - spot.y);
          const joinX =
            targetRight === null
              ? null
              : joinStretch('x', spot, spot.x + stagedW, spot.y, spot.y + stagedH, others);
          // A joint the size floor would refuse is discarded whole, not floored into some third
          // length that is neither what the hand asked for nor a joint.
          if (joinX && joinX.at - spot.x >= minW) {
            targetRight = joinX.at;
            rightGuide = joinX.guide;
          }
          const joinY =
            targetBottom === null
              ? null
              : joinStretch('y', spot, spot.y + stagedH, spot.x, spot.x + stagedW, others);
          if (joinY && joinY.at - spot.y >= minH) {
            targetBottom = joinY.at;
            bottomGuide = joinY.guide;
          }
        }
        const width = targetRight === null ? spot.width : Math.max(minW, targetRight - spot.x);
        const height = targetBottom === null ? spot.height : Math.max(minH, targetBottom - spot.y);
        // The area's edge stops a stretch the way it stops a drag — and, as everywhere else, only
        // for a shape that was honoring that edge when its handle was grabbed. This used to clamp
        // whenever any room at all remained, and the owner's bar top is what that cost: a counter
        // wider than the bar interior was cut down to whatever room was left of the edge the
        // instant its handle was touched — it shrank on the spot, no drag could take it back past
        // the edge it had already crossed, and even a drag on the depth handle alone rewrote its
        // length. A shape past the edge is something the owner is midway through sorting out —
        // about to stretch the area, or about to shrink the shape — so it stretches freely and is
        // counted as outside instead, which is the state he can see and act on.
        const roomW = limitW !== null && spot.x + resizing.startW <= limitW ? limitW - spot.x : null;
        const roomH = limitH !== null && spot.y + resizing.startH <= limitH ? limitH - spot.y : null;
        const finalW = roomW === null ? width : Math.max(minW, Math.min(width, roomW));
        const finalH = roomH === null ? height : Math.max(minH, Math.min(height, roomH));
        // The line only stays up if the edge really got there — a stretch stopped by the area's
        // edge or by the smallest allowed size must not leave a guide claiming otherwise.
        setGuides(
          drawnGuides(
            targetRight !== null && spot.x + finalW === targetRight ? rightGuide : null,
            targetBottom !== null && spot.y + finalH === targetBottom ? bottomGuide : null,
            spot.x,
            spot.y,
            finalW,
            finalH,
          ),
        );
        onResize(resizing.id, finalW, finalH);
        return;
      }
      if (marquee) {
        setMarquee({ ...marquee, x: e.clientX - surface.left, y: e.clientY - surface.top });
        return;
      }
      if (!dragging || !onMove) return;
      const spot = spots.find((s) => s.id === dragging.id);
      const from = dragging.origin.find((o) => o.id === dragging.id);
      if (!spot || !from) return;
      // Nothing in the group lines itself up with the rest of the group: they are all moving, so a
      // wall would snap to the wall travelling beside it and the pair would weld themselves shut.
      const others = spots.filter((s) => !dragging.origin.some((o) => o.id === s.id));
      const rawX = e.clientX - surface.left - dragging.dx;
      const rawY = e.clientY - surface.top - dragging.dy;
      const across = settle(
        rawX,
        [rawX, rawX + spot.width],
        rawX + spot.width / 2,
        linesAlong(others, 'x', limitW, limitH),
        free,
      );
      const down = settle(
        rawY,
        [rawY, rawY + spot.height],
        rawY + spot.height / 2,
        linesAlong(others, 'y', limitH, limitW),
        free,
      );
      // Between runs of the same kind, the joint pass outranks the grid and the alignment lines:
      // an end carried to within one square of another end shuts flush, which is the only thing a
      // hand bringing two counter ends that close together can mean. See joinRun and JOIN.
      const joined = free ? null : joinRun(spot, across.value, down.value, others);
      // One shift, worked out from the shape under the finger and given to everything picked. The
      // area's edge is applied to the GROUP's outline rather than to each shape, so six stools
      // pushed into a wall stop as a row instead of piling up on top of each other against it.
      let shiftX = (joined ? joined.x : across.value) - from.x;
      let shiftY = (joined ? joined.y : down.value) - from.y;
      // Only hold the group back when it actually fits; a selection wider than the room would
      // otherwise be dragged hard against the left edge and stick there.
      const groupW = dragging.bounds.maxX - dragging.bounds.minX;
      const groupH = dragging.bounds.maxY - dragging.bounds.minY;
      if (limitW !== null && groupW < limitW) shiftX = Math.min(shiftX, limitW - dragging.bounds.maxX);
      if (limitH !== null && groupH < limitH) shiftY = Math.min(shiftY, limitH - dragging.bounds.maxY);
      shiftX = Math.max(shiftX, -dragging.bounds.minX);
      shiftY = Math.max(shiftY, -dragging.bounds.minY);
      const x = from.x + shiftX;
      const y = from.y + shiftY;
      // Guides follow the position that was actually kept, so a shape stopped by the edge of the
      // area does not leave a line hanging where it wanted to go. A shut joint draws both of its
      // edges — the cross at the corner is the report that the two runs are now flush.
      setGuides(
        joined
          ? x === joined.x && y === joined.y
            ? drawnGuides(joined.toX, joined.toY, x, y, spot.width, spot.height)
            : []
          : drawnGuides(
              x === across.value ? across.guide : null,
              y === down.value ? down.guide : null,
              x,
              y,
              spot.width,
              spot.height,
            ),
      );
      // The moment anything actually shifts, the press was a drag of the group and not a click on
      // one of it. A tremor that snaps back to where it started still counts as a click.
      if (x !== from.x || y !== from.y) narrowTo.current = null;
      for (const o of dragging.origin) onMove(o.id, o.x + shiftX, o.y + shiftY);
    },
    [dragging, limitH, limitW, marquee, onMove, onResize, onRoomResize, resizing, roomDrag, spots],
  );

  /**
   * Everything a drag on bare floor swept up.
   *
   * Touching counts, not enclosing. Lassoing a cluster of beach chairs by drawing a box that fully
   * contains every one of them means starting outside the furniture on all four sides, which on a
   * crowded plan there is usually no room to do.
   */
  const endDrag = useCallback(() => {
    if (marquee) {
      const x0 = Math.min(marquee.x0, marquee.x);
      const x1 = Math.max(marquee.x0, marquee.x);
      const y0 = Math.min(marquee.y0, marquee.y);
      const y1 = Math.max(marquee.y0, marquee.y);
      // Under a few pixels it was a click, not a lasso — and a click on bare floor means "nothing".
      const swept = x1 - x0 > 4 || y1 - y0 > 4;
      const hit = swept
        ? spots
            .filter((s) => s.x < x1 && s.x + s.width > x0 && s.y < y1 && s.y + s.height > y0)
            .map((s) => s.id)
        : [];
      onSelect?.([...new Set([...marquee.keep, ...hit])]);
      setMarquee(null);
    }
    // A press on one of several that never went anywhere: it was a click, and a click picks the one
    // thing you clicked. Without this the only way down from six to one is to click bare floor
    // first, and "Remove these 6" sits where the owner is expecting "Remove it".
    if (narrowTo.current) {
      onSelect?.([narrowTo.current]);
      narrowTo.current = null;
    }
    setDragging(null);
    setResizing(null);
    setGuides([]);
  }, [marquee, onSelect, spots]);

  return (
    <Box
      ref={surfaceRef}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
      // Bare floor only, and only with the palette idle: with something armed a press on the floor
      // means put it there, and a lasso appearing under the palm you were placing would be the two
      // gestures fighting each other. A press on a shape never reaches here — it starts a move.
      onPointerDown={(e) => {
        if (placing || !onSelect || e.target !== e.currentTarget) return;
        const surface = surfaceRef.current?.getBoundingClientRect();
        if (!surface) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        const x = e.clientX - surface.left;
        const y = e.clientY - surface.top;
        setMarquee({
          x0: x,
          y0: y,
          x,
          y,
          keep: e.shiftKey || e.metaKey || e.ctrlKey ? picked : [],
        });
      }}
      // Armed from the palette: one click drops it. Without something armed a single click on the
      // floor still just clears the selection, so rearranging never places anything by accident.
      onClick={(e) => {
        if (!placing || !onAddAt || readOnly || e.target !== e.currentTarget) return;
        const surface = surfaceRef.current?.getBoundingClientRect();
        if (!surface) return;
        // A click past the edge of a sized area lands just inside it rather than nowhere: something
        // appears where you aimed, and the outline goes on meaning what it says.
        onAddAt(
          fitInside(snap(e.clientX - surface.left), 0, limitW),
          fitInside(snap(e.clientY - surface.top), 0, limitH),
        );
      }}
      style={{
        position: 'relative',
        // Crosshair over bare floor is the hint that a drag out here gathers things up rather than
        // doing nothing. Shapes set their own grab cursor, so it only shows where it is true.
        cursor: placing ? 'copy' : onSelect ? 'crosshair' : undefined,
        width: shaped ? extentX : '100%',
        minWidth: extentX,
        height: extentY,
        borderRadius: 8,
        // With no size of its own the whole surface IS the room, grid and all. With one, the grid
        // and the border move onto the drawn area below and everything out here reads as off it.
        border: shaped ? '1px dashed transparent' : `1px solid ${brand.haze}`,
        backgroundColor: shaped ? 'transparent' : '#fbfaf7',
        // A faint grid makes the snapping legible instead of mysterious.
        backgroundImage: room
          ? undefined
          : `linear-gradient(${brand.haze} 1px, transparent 1px), linear-gradient(90deg, ${brand.haze} 1px, transparent 1px)`,
        backgroundSize: `${SNAP * 2}px ${SNAP * 2}px`,
        touchAction: 'none',
        overflow: 'hidden',
      }}
    >
      {shaped && onRoomResize && !readOnly && (
        // The room's own corner. Everything else on this plan can be stretched by dragging; the room
        // could only be typed at, with nothing on screen saying which numbers were right.
        //
        // A real <button> rather than a div wearing role="button": it was already focusable, so the
        // only thing the div bought was having to restate what a button is. Dragging is a pointer
        // affordance and there is no keyboard equivalent on the corner itself — the width and height
        // boxes above the plan are the keyboard path, and they are why this is an addition to that
        // rather than the only way in.
        <button
          type="button"
          aria-label="Resize the area"
          title="Drag to resize the area"
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            setRoomDrag(true);
          }}
          onPointerUp={(e) => {
            e.currentTarget.releasePointerCapture(e.pointerId);
            setRoomDrag(false);
          }}
          style={{
            position: 'absolute',
            left: (limitW ?? 0) - HANDLE_HIT / 2,
            top: (limitH ?? 0) - HANDLE_HIT / 2,
            width: HANDLE_HIT,
            height: HANDLE_HIT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'nwse-resize',
            zIndex: 3,
            // A button brings padding, a border and a background of its own; the handle is the
            // square inside it, so all three go.
            padding: 0,
            border: 'none',
            background: 'transparent',
            appearance: 'none',
          }}
        >
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              background: '#fff',
              border: `2px solid ${brand.coral}`,
            }}
          />
        </button>
      )}

      {shaped && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: limitW ?? 0,
            height: limitH ?? 0,
            borderRadius: 8,
            border: `2px solid ${brand.haze}`,
            backgroundColor: '#fbfaf7',
            backgroundImage: `linear-gradient(${brand.haze} 1px, transparent 1px), linear-gradient(90deg, ${brand.haze} 1px, transparent 1px)`,
            backgroundSize: `${SNAP * 2}px ${SNAP * 2}px`,
            // The floor is scenery for the click handler above, which only fires on bare surface.
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}

      {spots.map((spot) => {
        const selected = selection.has(spot.id);
        // Everything travelling with the drag lifts and stops easing, not just the one under the
        // finger — otherwise the rest of the group slides along a frame behind it.
        const carried = dragging?.origin.some((o) => o.id === spot.id) ?? false;
        const movable = spot.mobility === 'movable';
        // Scenery: drawn, labeled, movable by the designer — but never a seat.
        const isDecor = spot.assignable === false;
        const decor = isDecor ? decorStyle(spot.kind) : null;
        /*
         * WHICH COLUMN GOVERNS THE DRAWING IS NOT DECIDED HERE. The rule is generated from
         * contract/floor-art.json and the register calls its twin on the same row. This screen used
         * to decide it inline and so did the app, from different columns, and a palm came out a
         * rosette here and a green disc on his phone. A shared statement that one of the two
         * renderers does not actually call is the same defect with more ceremony around it, so this
         * is the call. See scripts/check-floor-art-rule.mjs.
         *
         * Runs are the one thing answered before the shared rule is asked: the wall, the bar top and
         * the dock are joined end to end and this screen draws them in their own pixels so flush
         * joints truly close. The shared table's whole-box drawing of each of them is for the
         * register, which does not weld and does not need to. See weldsFor and runArt.
         */
        const welds = isDecor && isRunKind(spot.kind) ? weldsFor(spot, spots, selection) : null;
        const run = welds
          ? runArt(
              spot.kind,
              spot.width,
              spot.height,
              spot.flipped ? flipWelds(welds, spot.width, spot.height) : welds,
            )
          : undefined;
        const artKey = run ? null : artKeyFor(spot.kind, spot.shape, !isDecor);
        const entry = artKey ? artEntry(artKey) : undefined;
        const art = entry && orientationOf(entry, spot.width, spot.height);
        const drawn = Boolean(art || run);
        // One set of paints for both ways of drawing, so a run and a stretched silhouette can
        // never disagree about what selected or off-the-plan looks like.
        const fillPaint = !spot.active ? '#e9e5dc' : selected ? brand.sea : (decor?.fill ?? '#ffffff');
        const edgePaint = selected ? brand.deep : spot.active ? (decor?.edge ?? brand.gray) : '#cfc9bd';
        const detailPaint = selected ? brand.sand : (decor?.edge ?? brand.gray);
        // Only scenery ever arrives unnamed, and it is the thing most in need of being named out
        // loud: on the plan a dock is told apart from a wall by its silhouette, but a tooltip and a
        // screen reader have only this.
        const name = spot.label.trim() || kindLabel(spot.kind);
        return (
          <Box
            key={spot.id}
            role={onSelect ? 'button' : undefined}
            tabIndex={onSelect ? 0 : undefined}
            // A place taken off the plan is still drawn — that is the only way back to it — so it
            // has to say so. Dimmed gray alone was indistinguishable from scenery, and a manager
            // reading the map counted a table nobody could be seated at.
            aria-label={
              !spot.active
                ? `${name} — off the plan, nobody can be seated here`
                : isDecor
                  ? `${name} — part of the room, not a seat`
                  : `${name}, seats ${spot.seats}${movable ? ', movable' : ', fixed in place'}`
            }
            title={
              !spot.active
                ? `${name} — off the plan. Right-click to put it back.`
                : isDecor
                  ? `${name} — no tab can be opened here`
                  : `${name} · ${spot.seats} ${spot.seats === 1 ? 'seat' : 'seats'}${spot.combinable ? ' · can be joined' : ''}`
            }
            aria-pressed={onSelect ? selected : undefined}
            onPointerDown={(e) => handlePointerDown(e, spot)}
            onDoubleClick={(e) => {
              e.stopPropagation();
              onOpen?.(spot.id);
            }}
            onContextMenu={(e) => {
              if (!onOpen) return;
              // The owner asked for right-click here specifically, and a floor plan is the one
              // place where the browser's own menu has nothing useful to offer.
              e.preventDefault();
              e.stopPropagation();
              // Right-clicking one of several picked things opens THAT one without throwing the
              // rest away, so the menu is still reachable in the middle of arranging a group.
              if (!selection.has(spot.id)) onSelect?.([spot.id]);
              onOpen(spot.id);
            }}
            onKeyDown={(e) => {
              if (!onSelect) return;
              if (e.key === 'Enter') {
                e.preventDefault();
                onSelect([spot.id]);
                onOpen?.(spot.id);
                return;
              }
              if (e.key === ' ') {
                e.preventDefault();
                // Space is the keyboard's shift-click: it is how a second and third thing get
                // picked without a mouse, so it toggles rather than starting over.
                onSelect(
                  selection.has(spot.id) ? picked.filter((id) => id !== spot.id) : [...picked, spot.id],
                );
                return;
              }
              // Arrow keys are how this is usable without a mouse — and on a trackpad they are
              // often faster than dragging for a small correction.
              if (readOnly || !onMove) return;
              const step = e.shiftKey ? SNAP * 2 : SNAP;
              const deltas: Record<string, [number, number]> = {
                ArrowLeft: [-step, 0],
                ArrowRight: [step, 0],
                ArrowUp: [0, -step],
                ArrowDown: [0, step],
              };
              const delta = deltas[e.key];
              if (!delta) return;
              e.preventDefault();
              // A nudge moves everything picked, for the same reason a drag does — and the group
              // stops at the top and left edges as one, so the arrangement survives the wall.
              const nudging = selection.has(spot.id) ? spots.filter((s) => selection.has(s.id)) : [spot];
              const holdX = Math.max(-Math.min(...nudging.map((s) => s.x)), delta[0]);
              const holdY = Math.max(-Math.min(...nudging.map((s) => s.y)), delta[1]);
              for (const s of nudging) onMove(s.id, s.x + holdX, s.y + holdY);
            }}
            style={{
              position: 'absolute',
              left: spot.x,
              top: spot.y,
              width: spot.width,
              height: spot.height,
              borderRadius: drawn ? 0 : cssRadius(spot.shape),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              cursor: readOnly ? 'pointer' : carried ? 'grabbing' : 'grab',
              userSelect: 'none',
              // Who wins the press when things overlap. The owner's new bar top lay over the
              // corner of a table, and the table — visibly picked — could not be dragged: the
              // counter was drawn later, so it sat on top and took every press meant for the
              // table under it. What is picked answers to the hand before anything else does,
              // and seating outranks scenery, because a wall is a backdrop for the furniture,
              // not a lid over it.
              zIndex: selected ? 3 : isDecor ? 1 : 2,
              // A hexagon is painted by the SVG below instead: `clip-path` would cut the border
              // off, and the border is what carries fixed-versus-movable at a glance.
              backgroundColor: drawn ? 'transparent' : fillPaint,
              color: selected && spot.active && !drawn ? brand.sand : (decor?.text ?? brand.ink),
              // A dashed edge reads as "not nailed down" at a glance; solid means anchored.
              border: drawn
                ? 'none'
                : selected
                  ? `2px solid ${brand.deep}`
                  : decor
                    ? `1px solid ${decor.edge}`
                    : `2px ${movable ? 'dashed' : 'solid'} ${spot.active ? brand.gray : '#cfc9bd'}`,
              boxShadow: carried ? '0 6px 16px rgba(0,0,0,0.18)' : undefined,
              opacity: spot.active ? 1 : 0.7,
              transition: carried ? undefined : 'left 80ms linear, top 80ms linear',
            }}
          >
            {run && (
              // A run's canvas is its own pixels, one-to-one, not a stretched 100-square: the
              // outline must sit the same distance inside every run for two welded runs to read
              // as one piece, and a percentage inset cannot do that. See runArt.
              <svg
                viewBox={`0 0 ${spot.width} ${spot.height}`}
                preserveAspectRatio="none"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              >
                <title>{name}</title>
                {/* Mirrored is the same drawing given a half-turn inside the same footprint — the
                    weld spans were turned first so the joint gaps land back where the neighbors
                    really are; see flipWelds. */}
                <g transform={spot.flipped ? `rotate(180 ${spot.width / 2} ${spot.height / 2})` : undefined}>
                  <path d={run.fill} fill={fillPaint} />
                  <path
                    d={run.outline}
                    fill="none"
                    stroke={edgePaint}
                    strokeWidth={2}
                    // Round caps close the outline's corners where two edges meet as separate
                    // strokes, and finish the cut ends beside a welded joint cleanly.
                    strokeLinecap="round"
                    strokeDasharray={movable && !selected ? '6 4' : undefined}
                  />
                  <path
                    d={run.detail}
                    fill="none"
                    stroke={detailPaint}
                    strokeWidth={1.5}
                    opacity={0.75}
                    strokeLinecap="round"
                  />
                </g>
              </svg>
            )}
            {art && (
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              >
                <title>{name}</title>
                {/* Mirrored: a half-turn about the authored square's center, which the stretch to
                    the footprint then keeps centered — the sign's arrow points the other way, the
                    lounger's backrest moves to the other end, and the footprint never moves. */}
                <g transform={spot.flipped ? 'rotate(180 50 50)' : undefined}>
                  <path
                    d={art.path}
                    fill={fillPaint}
                    stroke={edgePaint}
                    strokeWidth={2}
                    // vectorEffect keeps the outline one weight however the shape is scaled, so a
                    // six-top and a bar stool read as the same kind of drawing.
                    vectorEffect="non-scaling-stroke"
                    strokeDasharray={movable && !selected ? '6 4' : undefined}
                    strokeLinejoin="round"
                  />
                  {art.detail && (
                    <path
                      d={art.detail}
                      fill="none"
                      stroke={detailPaint}
                      strokeWidth={1.5}
                      vectorEffect="non-scaling-stroke"
                      opacity={0.75}
                      strokeLinecap="round"
                    />
                  )}
                </g>
              </svg>
            )}
            <Text
              c={drawn && selected && spot.active ? brand.sand : undefined}
              size={spot.width >= 56 ? 'xs' : '11px'}
              fw={700}
              lh={1.1}
              ta="center"
              style={{
                pointerEvents: 'none',
                overflow: 'hidden',
                position: 'relative',
                textDecoration: spot.active ? undefined : 'line-through',
              }}
            >
              {fitLabel(spot.label, spot.width)}
            </Text>
            {!isDecor && spot.height >= 44 && (
              <Text size="10px" lh={1} style={{ pointerEvents: 'none', opacity: 0.75, position: 'relative' }}>
                {spot.seats} {spot.seats === 1 ? 'seat' : 'seats'}
              </Text>
            )}
            {!isDecor && spot.combinable && spot.height >= 44 && (
              <Text size="9px" lh={1} style={{ pointerEvents: 'none', opacity: 0.75, position: 'relative' }}>
                joinable
              </Text>
            )}

            {/* Stretching a wall by typing 200 into a box, closing the dialog, looking, and opening
                it again to try 240 is the wrong instrument for a decision you make by eye. Handles
                only on the selected thing, so a plan with thirty places on it is not covered in
                grab points. Deliberately not reachable by keyboard — the arrow keys are already
                how a place is nudged without a mouse, and giving them a second meaning would make
                the obvious one ambiguous; the Across and Deep boxes in the dialog remain the
                keyboard route to an exact size.

                Only when ONE thing is picked: with a group, the handles of six shapes overlap the
                shapes next to them, and the first drag stretches one of them while the owner was
                trying to move the lot. */}
            {selected &&
              selection.size === 1 &&
              !readOnly &&
              onResize &&
              handlesFor(spot.width, spot.height).map((handle) => (
                <Box
                  key={handle.axis}
                  aria-hidden="true"
                  onPointerDown={(e) => handleResizeDown(e, spot, handle.axis)}
                  onDoubleClick={(e) => e.stopPropagation()}
                  onContextMenu={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    width: HANDLE_HIT,
                    height: HANDLE_HIT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: handle.cursor,
                    touchAction: 'none',
                    zIndex: 3,
                    ...handle.style,
                  }}
                >
                  <span
                    style={{
                      width: HANDLE_DOT,
                      height: HANDLE_DOT,
                      borderRadius: 4,
                      backgroundColor: '#ffffff',
                      border: `2px solid ${brand.deep}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
                    }}
                  />
                </Box>
              ))}
          </Box>
        );
      })}

      {/* Coral, because everything else on this plan is teal, sand or gray — a guide has to be
          unmistakably not part of the room. Above every shape, the picked one included: the line
          is the report of where the shape under the pointer is about to land. */}
      {guides.map((guide) => (
        <div
          key={`${guide.axis}-${guide.at}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            backgroundColor: brand.coral,
            pointerEvents: 'none',
            zIndex: 4,
            ...(guide.axis === 'x'
              ? { left: guide.at, top: guide.from, width: 1, height: Math.max(1, guide.to - guide.from) }
              : { top: guide.at, left: guide.from, height: 1, width: Math.max(1, guide.to - guide.from) }),
          }}
        />
      ))}

      {/* The box being swept, drawn over everything so it is obvious what it has reached. */}
      {marquee && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: Math.min(marquee.x0, marquee.x),
            top: Math.min(marquee.y0, marquee.y),
            width: Math.abs(marquee.x - marquee.x0),
            height: Math.abs(marquee.y - marquee.y0),
            border: `1px solid ${brand.sea}`,
            backgroundColor: 'rgba(14,124,123,0.10)',
            borderRadius: 4,
            pointerEvents: 'none',
            zIndex: 5,
          }}
        />
      )}

      {/* Only while something is actually being moved. A shape that jumps the last few pixels on its
          own needs to say, once, that it can be told not to — and then get out of the way. */}
      {(dragging || resizing) && (
        <Text
          size="10px"
          c={brand.gray}
          style={{
            position: 'absolute',
            bottom: 6,
            left: 8,
            zIndex: 4,
            pointerEvents: 'none',
            backgroundColor: 'rgba(255,255,255,0.9)',
            borderRadius: 4,
            padding: '2px 6px',
          }}
        >
          Lines up with what is near it — hold Option (Alt) to place it freely.
        </Text>
      )}

      {spots.length === 0 && (
        <Text c="dimmed" size="sm" style={{ position: 'absolute', top: 16, left: 16, pointerEvents: 'none' }}>
          {/* It said "double-click to add a table" long after double-clicking stopped being how
              anything gets added — the palette above is, and it is the only thing that can put a
              wall down. An empty area telling you to do something that does nothing is worse than
              an empty area saying nothing. */}
          {onAddAt
            ? 'Nothing here yet — pick something above, then click the floor to put it down.'
            : 'Nothing in this area yet.'}
        </Text>
      )}
    </Box>
  );
}
